import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkingClinicianProfileStatus,
  setClinicianTenancy,
  setSearchFilter,
} from '../../store/ai2';
import { debounce } from 'lodash';
import type { RootState } from '../../store';
import type { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';
import { useTriggerUpdate } from '../../context/TriggerUpdateContext';
import { ClinicianProfileFieldTO, toClinicianProfileFieldTO } from '../../lib';

export const useClinicianProfile = () => {
  const navigate = useNavigate();
  const { triggerClinicianProfileUpdate } = useTriggerUpdate();
  const [tenancyOptions, setTenancyOptions] = useState<
    {
      labelKey: number;
      value: string | undefined;
    }[]
  >([]);
  const dispatch: Dispatch<AnyAction> = useDispatch();
  const { firstName, clinicianProfileStatus, tenancies, selectedTenancy } =
    useSelector((state: RootState) => state.clinicianProfile);

  const { filterState } = useSelector((state: RootState) => state.patientList);

  const emptyFilter = () => ({
    ...filterState,
    event_subcategory_name: '',
    favourite: false,
  });

  const clinicianProfileFields: ClinicianProfileFieldTO[] = useMemo(
    () =>
      (selectedTenancy?.features?.clinicianProfileFields || []).map(
        toClinicianProfileFieldTO,
      ),
    [selectedTenancy],
  );

  // Debounced update function
  const debouncedUpdateTenancy = useCallback(
    debounce((selectedKey: number) => {
      dispatch(checkingClinicianProfileStatus());
      const selectedTenancy = tenancies.find(
        (object) => object.tenancy_id === selectedKey,
      );
      dispatch(setClinicianTenancy(selectedTenancy || null));
      dispatch(checkingClinicianProfileStatus());
      dispatch(setSearchFilter(emptyFilter()));
      triggerClinicianProfileUpdate();
      navigate('/');
    }, 300),
    [dispatch, tenancies],
  );

  const updateTenancy = (props: {
    selectedKey: number[];
    selectedValue: string[];
  }) => {
    if (props?.selectedKey.length > 0) {
      debouncedUpdateTenancy(props.selectedKey[0]);
    }
  };

  useEffect(() => {
    const newTenancyOptions = tenancies.map((tenancy) => ({
      labelKey: tenancy.tenancy_id,
      value: tenancy.tenancy_name,
    }));
    setTenancyOptions(newTenancyOptions);
  }, [tenancies]);

  return {
    firstName,
    clinicianProfileStatus,
    tenancies,
    selectedTenancy,
    tenancyOptions,
    updateTenancy,
    clinicianProfileFields,
  };
};
